/**
 * @file DirectoryAutocomplete.tsx
 * @description Универсальный Autocomplete для справочников (Producer / Supplier).
 * phase08.5 shadcn.
 *
 * Замечание владельца 2026-10-19: уже выбранное значение можно снять — первым
 * пунктом списка «Отсутствует» (`clearLabel`), выбор отдаёт `onChange("")`.
 */

import { Combobox, type ComboboxOption } from "../ui/combobox";

export interface DirectoryItem {
  id: string;
  displayName: string;
  isActive: boolean;
}

interface Props<T extends DirectoryItem> {
  items: T[];
  value: string;
  /** Выбранный id; `""` — значение снято пунктом `clearLabel`. */
  onChange: (id: string) => void;
  onCreateClick?: () => void;
  label?: string;
  /** Backward compat — игнорируется. Используй <Label> снаружи. */
  helperText?: string;
  placeholder?: string;
  /** Пункт «без значения» первым в списке; по умолчанию «Отсутствует». */
  clearLabel?: string;
  disabled?: boolean;
  renderSecondary?: (item: T) => string | undefined;
}

export function DirectoryAutocomplete<T extends DirectoryItem>({
  items,
  value,
  onChange,
  onCreateClick,
  placeholder = "Выбрать…",
  clearLabel = "Отсутствует",
  disabled,
  renderSecondary,
}: Props<T>) {
  const sorted = [...items].sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return a.displayName.localeCompare(b.displayName);
  });

  const options: ComboboxOption[] = sorted.map((item) => {
    const sub = renderSecondary ? renderSecondary(item) : undefined;
    return {
      value: item.id,
      label: item.displayName + (!item.isActive ? " · неактивен" : ""),
      description: sub,
    };
  });

  return (
    <Combobox
      options={options}
      value={value || null}
      onChange={(v) => onChange(v ?? "")}
      placeholder={placeholder}
      searchPlaceholder="Найти…"
      emptyText="Не найдено"
      clearLabel={clearLabel}
      createLabel={onCreateClick ? "Создать новый…" : undefined}
      onCreateClick={onCreateClick}
      disabled={disabled}
    />
  );
}
